

import { Financials, VendorTotals, ServiceTotals, fmt } from "./types";
import { TrendingUp, TrendingDown } from "lucide-react";

interface KeyMetricsProps {
  financials: Financials;
  vendorTotals: VendorTotals;
  serviceTotals: ServiceTotals;
}

export function KeyMetrics({ financials, vendorTotals, serviceTotals }: KeyMetricsProps) {
  const f = financials;
  const profit = f.profit ?? 0;
  const netRevenue = f.net_revenue ?? 0;
  const profitMargin = netRevenue > 0 ? ((profit / netRevenue) * 100).toFixed(1) : "0.0";
  const totalCosts = f.total_expense ?? serviceTotals.total_cost + vendorTotals.total_assigned + f.adhoc_cost;
  const costRatio = netRevenue > 0 ? (totalCosts / netRevenue) * 100 : 0;

  const metrics = [
    {
      label: "Revenue (before GST)",
      value: fmt(f.net_revenue),
      sublabel: f.tax_amount > 0 ? `+ GST ${fmt(f.tax_amount)} = billed ${fmt(f.final_revenue)}` : "No GST on this event",
      bg: "bg-emerald-50",
      border: "border-emerald-200",
      text: "text-emerald-700",
      subtext: "text-emerald-500",
      progressValue: null,
    },
    {
      label: "Cost",
      value: fmt(totalCosts),
      sublabel: `${Math.round(costRatio)}% of revenue`,
      bg: "bg-rose-50",
      border: "border-rose-200",
      text: "text-rose-700",
      subtext: "text-rose-500",
      progressValue: Math.min(costRatio, 100),
    },
    {
      label: profit >= 0 ? "Profit" : "Loss",
      value: fmt(Math.abs(profit)),
      sublabel: `${profitMargin}% margin`,
      bg: profit >= 0 ? "bg-emerald-50" : "bg-rose-50",
      border: profit >= 0 ? "border-emerald-200" : "border-rose-200",
      text: profit >= 0 ? "text-emerald-700" : "text-rose-700",
      subtext: profit >= 0 ? "text-emerald-500" : "text-rose-500",
      progressValue: Math.min(Math.max(parseFloat(profitMargin), 0), 100),
      showTrend: true,
    },
    {
      label: "Owed to vendors",
      value: fmt(vendorTotals.total_balance),
      sublabel: `Paid so far ${fmt(vendorTotals.total_paid)}`,
      bg: "bg-amber-50",
      border: "border-amber-200",
      text: "text-amber-700",
      subtext: "text-amber-500",
      progressValue: null,
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {metrics.map((metric) => (
        <div
          key={metric.label}
          className={`${metric.bg} rounded-xl p-3 sm:p-4 border ${metric.border} transition-all hover:shadow-md`}
        >
          <div className={`text-xs ${metric.subtext} font-semibold mb-1.5 uppercase tracking-wide`}>{metric.label}</div>
          <div className={`text-base sm:text-xl font-bold ${metric.text} truncate flex items-center gap-1.5`}>
            {metric.value}
            {metric.showTrend && profit >= 0 && <TrendingUp className="w-4 h-4 text-emerald-600" />}
            {metric.showTrend && profit < 0 && <TrendingDown className="w-4 h-4 text-rose-600" />}
          </div>
          {metric.progressValue !== null && (
            <div className="mt-2 w-full bg-stone-200 rounded-full h-1.5 overflow-hidden">
              <div
                className={`h-full transition-all ${
                  metric.label === "Profit" || metric.label === "Loss"
                    ? "bg-emerald-500"
                    : "bg-rose-500"
                }`}
                style={{ width: `${metric.progressValue}%` }}
              />
            </div>
          )}
          <div className={`text-xs ${metric.subtext} mt-1.5 truncate`}>{metric.sublabel}</div>
        </div>
      ))}
    </div>
  );
}
